import {
	Button,
	Checkbox,
	Input,
	Popover,
	Select,
	Tabs,
	Tag,
	Upload,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';

import Icon from '../Icon/Icon';
import Popup from '../Popup/Popup';

import { formattedMentoringScopes as mentoringScopes } from '../../Helpers/StaticData';
import useCreateResource from './useCreateResource';

import { Props, Participant } from './CreateResource.model';

import styles from './CreateResource.module.scss';
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';
import Spinner from '../Spinner/Spinner';

const CreateResource = ({ author, onClose, recordingId }: Props) => {
	const {
		addParticipant,
		canAddApplicantMentees,
		control,
		errors,
		handleSubmit,
		onChangeFile,
		onCreateResource,
		removeParticipant,
		setIsAdminAuthor,
		state: { file, fileTooLarge , isAdminAuthor, loading, members, participants },
	} = useCreateResource({ author, onClose, recordingId });
	const intl = useIntl();
	const { role } = useAuthorizationDataContext();

	const { TextArea } = Input;
	const { TabPane } = Tabs;
	const { Option } = Select;

	const renderParticipant = (
		onClick: (item: Participant) => void,
		participant: Participant,
		index: number
	) => (
		<div
			className={styles.participant}
			key={`mentor${index}`}
			onClick={onClick.bind(null, participant)}
		>
			{participant.imageId ? (
				<div className={styles.avatar}>
					<img alt="Profile" src={participant.imageId} />
				</div>
			) : (
				<div className={styles.circle}>
					<Icon name="user" width="12px" color="text" />
				</div>
			)}
			{participant.firstName} {participant.lastName}
		</div>
	);

	return (
		<Popup
			actions={
				<>
					<Button size="large" onClick={onClose}><FormattedMessage id="buttons.cancel" /></Button>
					<Button size="large" type="primary" onClick={handleSubmit(onCreateResource)} disabled={!file?.name && !recordingId || fileTooLarge}><FormattedMessage id="buttons.save" /></Button>
				</>
			}
			size="large"
			title={{ target: 'addResource' }}
			onClose={onClose}
		>
			<>
				<Spinner delay={0} spinning={loading} fixed={false}/>
			<div className={styles.wrapper}>
				<div className={styles.container}>
					<div className={styles.sectionTitle}>
						<Icon
							name="file-alt"
							color="light"
							width="18"
							height="18"
							className={styles.icon}
						/>
						<h2>
							<FormattedMessage id="createResource.resourceSection" />
						</h2>
					</div>
					<div className={styles.fieldWrapper}>
						<label className={styles.floatingLabel}>
							<FormattedMessage id="formLabels.author" />
						</label>
						<Controller
							name="author"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									className={errors['author'] && 'error'}
									placeholder={intl.formatMessage({
										id: `formPlaceholders.author`,
									})}
									style={{ width: '100%' }}
									disabled={isAdminAuthor && role === 'Admin'}
								>
									{author ? (
										<Option value={author.userId}>
											{author.firstName} {author.lastName}
										</Option>
									) : (
										members
											.filter((item) => item.role === 'Mentor')
											.map((mentor) => (
												<Option value={mentor.userId}>
													{mentor.firstName} {mentor.lastName}
												</Option>
											))
									)}
								</Select>
							)}
							rules={{ required: !isAdminAuthor }}
						/>
					</div>
					{role === 'Admin' && (
						<div className={styles.fieldWrapper}>
							<Checkbox
								checked={isAdminAuthor}
								onChange={(e) => setIsAdminAuthor(e.target.checked)}
							>
								<FormattedMessage id="formLabels.adminAuthor" />
							</Checkbox>
						</div>
					)}
					<div className={styles.fieldWrapper}>
						<label className={styles.floatingLabel}>
							<FormattedMessage id="formLabels.title" />
						</label>
						<Controller
							name="title"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									className={errors['title'] && 'error'}
									placeholder={intl.formatMessage({
										id: `formPlaceholders.title`,
									})}
								/>
							)}
							rules={{ required: true }}
						/>
					</div>

					{!recordingId && (
						<div className={styles.fieldWrapper}>
							<label className={styles.floatingLabel}>
								<FormattedMessage id="formLabels.source" />
							</label>

							<Upload
								accept=".pdf, .docx, .mp4, .mov, .3gp, .webm, .mp3, .aac, .wav, .flac, .ogg, .wma, .aiff"
								showUploadList={false}
								onChange={onChangeFile}
								customRequest={() => {}}
							>
								<Input
									placeholder={intl.formatMessage({
										id: 'formPlaceholders.upload',
									})}
									value={file?.name}
								/>
							</Upload>
							{fileTooLarge && <div className={styles.errorMessage}><FormattedMessage id="createResource.error.fileTooLarge"/></div>}
						</div>
					)}

					<div className={styles.fieldWrapper}>
						<label className={styles.floatingLabel}>
							<FormattedMessage id="formLabels.description" />
						</label>
						<Controller
							name="description"
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									className={errors['description'] && 'error'}
									placeholder={intl.formatMessage({
										id: `formPlaceholders.description`,
									})}
									rows={5}
								/>
							)}
							rules={{ required: true }}
						/>
					</div>
					<div className={styles.fieldWrapper}>
						<Controller
							name="isForApplicantMentees"
							control={control}
							render={({ field }) => (
								<Checkbox {...field} checked={field.value}>
									<FormattedMessage id="formLabels.isForApplicantMentee" />
								</Checkbox>
							)}
						/>
					</div>

					<div className={styles.sectionTitle}>
						<Icon
							name="flask-potion"
							color="light"
							width="18"
							height="18"
							className={styles.icon}
							type="solid"
						/>
						<h2>
							<FormattedMessage id="createResource.researchAreaSection" />
						</h2>
					</div>
					<div className={styles.fieldWrapper}>
						<label className={styles.floatingLabel}>
							<FormattedMessage id="formLabels.keywords" />
						</label>
						<Controller
							name="researchFields"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									mode="tags"
									placeholder={intl.formatMessage({
										id: `formPlaceholders.keywords`,
									})}
									tagRender={({ label }) => (
										<Tag
											style={{
												border: 'none',
												textTransform: 'uppercase',
											}}
										>
											{label}
										</Tag>
									)}
									style={{ width: '100%' }}
								>
									{mentoringScopes.map((field) => (
										<Option key={field} value={field}>
											{field}
										</Option>
									))}
								</Select>
							)}
						/>
					</div>
				</div>

				<div className={styles.container}>
					<div className={styles.participantsContainer}>
						<div className={styles.sectionTitle}>
							<Icon
								name="user-tie"
								color="light"
								width="18"
								height="18"
								className={styles.icon}
							/>
							<h2>
								<FormattedMessage id="createResource.participantsSection" />
							</h2>
						</div>

						<Popover
							content={
								<Tabs
									className={styles.popoverTab}
									centered
									defaultActiveKey="1"
									size="small"
								>
									<TabPane tab={intl.formatMessage({id: "role.Mentors"})} key="1">
										{members
											.filter((item) => item.role === 'Mentor')
											.map(renderParticipant.bind(null, addParticipant))}
									</TabPane>
									<TabPane tab={intl.formatMessage({id: "role.Mentees"})} key="2">
										{members
											.filter((item) => item.role === 'Mentee')
											.map(renderParticipant.bind(null, addParticipant))}
										</TabPane>
										{canAddApplicantMentees() && <TabPane tab={intl.formatMessage({id: "role.ApplicantMentees"})} key="3">
										{members
											.filter((item) => item.role === 'ApplicantMentee')
											.map(renderParticipant.bind(null, addParticipant))}
									</TabPane>}
								</Tabs>
							}
							placement="rightTop"
							trigger="click"
						>
							<div className={styles.participant}>
								<span className={styles.circle}>
									<Icon name="plus" color="black" />
								</span>
								<FormattedMessage id="buttons.addParticipants" />
							</div>
						</Popover>
						{participants.map(renderParticipant.bind(null, removeParticipant))}
					</div>

					<div className={styles.divider}></div>

					<div className={styles.participantsContainer}>
						<div className={styles.sectionTitle}>
							<Icon
								name="comment-alt-lines"
								color="light"
								width="18"
								height="18"
								className={styles.icon}
								type="solid"
							/>
							<h2>
								<FormattedMessage id="createResource.commentsSection" />
							</h2>
						</div>
						<div className={styles.contactCheckbox}>
							<Controller
								name="acceptsComments"
								control={control}
								render={({ field }) => (
									<Checkbox
										className="contactCheckbox"
										{...field}
										checked={field.value}
									>
										<FormattedMessage id="formLabels.acceptsComments" />
									</Checkbox>
								)}
							/>
						</div>
					</div>
				</div>
			</div>
			</>
		</Popup>
	);
};

export default CreateResource;
